import React, { useEffect, useMemo, useState } from 'react';
import "./UserOffer.css";
import { BACKEND_URL } from "../../services/Helper";
import { toast } from "react-toastify";
import { useNavigate, useOutletContext } from "react-router-dom";
import { useDispatch, useSelector } from 'react-redux';
import { setUser } from '../../redux/userSlice';
import PreOrderModal from './PreOrderModal';
import ClaimOfferModal from './ClaimOfferModal';
import AOS from "aos";
import "aos/dist/aos.css";

const LEVEL_META = {
  3: { badge: '🔥 Promoted',  className: 'level-promoted' },
  2: { badge: '🏷️ Shop Deal', className: 'level-shop' },
  1: { badge: '⚡ Item Deal',  className: 'level-item' },
};
const LEVEL_SECTIONS = [
  { level: 3, heading: '🔥 Promoted Offers' },
  { level: 2, heading: '🏷️ Shop Deals' },
  { level: 1, heading: '⚡ Item Deals' },
];

const notifyA = (msg) => toast.error(msg);

function formatValidityDate(dateString) {
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

const UserOffer = () => {
  const navigate = useNavigate();
  const { location } = useOutletContext();
  const dispatch = useDispatch();
  const [useralloffer, setUseralloffer] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const user = useSelector((state) => state.nearbeUser.user);
  const [preOrderOpen, setPreOrderOpen] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [claimOpen, setClaimOpen] = useState(false);
  const [claimOffer, setClaimOffer] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("distance");   // 'distance' | 'discount'
  const [filterType, setFilterType] = useState("all"); // 'all' | 'shop' | 'item'

  const fetchUserData = async () => {
    try {
      const token = localStorage.getItem("nearbeusertoken");
      if (!token) return;
      const res = await fetch(`${BACKEND_URL}/nearbe/getuserdata`, {
        method: "GET",
        headers: { Authorization: token },
      });
      const data = await res.json();
      dispatch(setUser(data.userInformation));
    } catch (error) {
      console.error("Error:", error);
      toast.error("Something went wrong!");
    }
  };

  useEffect(() => { fetchUserData(); }, []);

  useEffect(() => {
    let mounted = true;
    const fetchAllOffer = async () => {
      setLoading(true);
      setError("");
      try {
        const params = new URLSearchParams();
        if (location?.lat != null) params.set('userLat', location.lat);
        if (location?.lon != null) params.set('userLng', location.lon);
        const res = await fetch(`${BACKEND_URL}/nearbe/getalloffer?${params}`);
        if (!res.ok) throw new Error("Failed to fetch offers");
        const data = await res.json();
        if (mounted) setUseralloffer(data.allOffer || []);
      } catch (err) {
        if (mounted) {
          setError("Something went wrong! Please try after some time");
          notifyA("Something went wrong! Please try after some time");
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };
    fetchAllOffer();
    return () => { mounted = false; };
  }, [location]);

  const filteredOffers = useMemo(() => {
    let list = useralloffer;

    // Type filter
    if (filterType === 'shop') list = list.filter(o => o.type === 'shop_offer');
    if (filterType === 'item') list = list.filter(o => o.type === 'item_offer');

    // Search
    const term = searchTerm.toLowerCase();
    if (term) {
      list = list.filter(o => {
        const shop = o?.postedBy?.shopName?.toLowerCase() || '';
        const title = (o.title || o.itemName || '').toLowerCase();
        return shop.includes(term) || title.includes(term);
      });
    }

    // Sort
    if (sortBy === 'discount') {
      list = [...list].sort((a, b) => {
        const pctA = a.type === 'item_offer' ? (a.offerPercentage || 0) : (a.percentage || 0);
        const pctB = b.type === 'item_offer' ? (b.offerPercentage || 0) : (b.percentage || 0);
        return pctB - pctA;
      });
    }
    // 'distance' keeps backend order (already sorted by proximity)

    return list;
  }, [useralloffer, searchTerm, sortBy, filterType]);

  const getGoogleMapsUrl = (restLat, restLon) => {
    return location
      ? `https://www.google.com/maps/dir/?api=1&origin=${location.lat},${location.lon}&destination=${restLat},${restLon}`
      : `https://www.google.com/maps/dir/?api=1&destination=${restLat},${restLon}`;
  };

  const handleClaimClick = (offer) => {
    const token = localStorage.getItem('nearbeusertoken');
    if (!token) {
      navigate('/userlogin');
      return;
    }
    setClaimOffer(offer);
    setClaimOpen(true);
  };

  const handlePreOrderClick = (offer) => {
    const token = localStorage.getItem("nearbeusertoken");
    if (token) {
      setSelectedOffer(offer);
      setPreOrderOpen(true);
    } else {
      navigate("/userlogin");
    }
  };

  useEffect(() => { AOS.init({ duration: 1200 }); }, []);

  if (loading) return <p className="no-offer-msg">⏳ Loading offers...</p>;
  if (error) return <p className="no-offer-msg">⚠️ {error}</p>;

  const renderCard = (alloffer) => {
    const isItemOffer = alloffer.type === 'item_offer';
    const levelMeta = LEVEL_META[alloffer.level] || LEVEL_META[2];
    const discountPct = isItemOffer ? alloffer.offerPercentage : alloffer.percentage;
    const title = isItemOffer ? alloffer.itemName : alloffer.title;
    const description = isItemOffer ? alloffer.itemDescription : alloffer.description;
    const dist = alloffer.distanceKm;

    return (
      <div key={alloffer._id} className={`useroffer-card ${levelMeta.className}`}>
        <div className="offer-card-header">
          <div className="offer-discount-zone">
            <span className="offer-pct">
              {discountPct ? `${discountPct}%` : "🎁"}
            </span>
            <span className="offer-pct-label">
              {discountPct ? "OFF" : "OFFER"}
            </span>
          </div>
          <div className="offer-header-right">
            <span className={`offer-level-badge ${levelMeta.className}`}>{levelMeta.badge}</span>
            <span className="userstatus active">● Active</span>
          </div>
        </div>

        <div className="offer-card-body">
          <h3 className="offer-title">{title}</h3>
          {isItemOffer && alloffer.price != null && (
            <p className="offer-item-price">
              ₹{Math.round(alloffer.price * (1 - alloffer.offerPercentage / 100))}
              <span className="offer-item-original"> ₹{alloffer.price}</span>
            </p>
          )}
          <h2
            className="userrestaurant-name"
            onClick={() =>
              navigate("/userdashboard/restaurants/info", {
                state: { hotels: alloffer.postedBy },
              })
            }
          >
            🏪 {alloffer?.postedBy?.shopName}
          </h2>
          {description && (
            <p className="useroffer-description">{description}</p>
          )}
        </div>

        <div className="offer-card-footer">
          <div className="offer-meta-chips">
            {!isItemOffer && alloffer.validTill && (
              <span className="useroffer-validity">
                📅 {formatValidityDate(alloffer.validTill)}
                {alloffer.endTime ? ` · ${alloffer.endTime}` : ""}
              </span>
            )}
            {dist != null && (
              <span className="offer-distance">📍 {dist} km away</span>
            )}
          </div>

          <div className="offer-card-actions">
            {!isItemOffer && (
              <button className="claim-offer-btn" onClick={() => handleClaimClick(alloffer)}>
                🎟️ Claim
              </button>
            )}
            {isItemOffer && (
              <button
                className="claim-offer-btn"
                onClick={() =>
                  navigate("/userdashboard/restaurants/info", {
                    state: { hotels: alloffer.postedBy },
                  })
                }
              >
                🏪 View Shop
              </button>
            )}
            {location &&
              typeof alloffer?.postedBy?.latitude === "number" &&
              typeof alloffer?.postedBy?.longitude === "number" && (
                <a
                  href={getGoogleMapsUrl(alloffer.postedBy.latitude, alloffer.postedBy.longitude)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="maps-link"
                >
                  🗺️ <span>Directions</span>
                </a>
              )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="useroffer-item" data-aos="fade-up">
      {/* Search + controls */}
      <div className="offer-search-bar">
        <span className="search-icon">🔍</span>
        <input
          type="text"
          placeholder="Search offers by shop or item name..."
          className="offer-search-input"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      <div className="offer-controls">
        {/* Sort */}
        <div className="offer-control-group">
          <span className="offer-control-label">Sort by</span>
          <div className="offer-sort-pills">
            <button
              className={`offer-sort-pill${sortBy === 'distance' ? ' active' : ''}`}
              onClick={() => setSortBy('distance')}
            >📍 Distance</button>
            <button
              className={`offer-sort-pill${sortBy === 'discount' ? ' active' : ''}`}
              onClick={() => setSortBy('discount')}
            >🔥 % Discount</button>
          </div>
        </div>

        {/* Type filter */}
        <div className="offer-control-group">
          <span className="offer-control-label">Show</span>
          <div className="offer-sort-pills">
            <button
              className={`offer-sort-pill${filterType === 'all' ? ' active' : ''}`}
              onClick={() => setFilterType('all')}
            >All</button>
            <button
              className={`offer-sort-pill${filterType === 'shop' ? ' active' : ''}`}
              onClick={() => setFilterType('shop')}
            >🏷️ Shop Offers</button>
            <button
              className={`offer-sort-pill${filterType === 'item' ? ' active' : ''}`}
              onClick={() => setFilterType('item')}
            >⚡ Item Deals</button>
          </div>
        </div>
      </div>

      {filteredOffers.length === 0 ? (
        <p className="no-offer-msg">🚫 No offers match your filters right now</p>
      ) : searchTerm || sortBy === 'discount' || filterType !== 'all' ? (
        /* Flat grid when searching / sorting by discount / type-filtered */
        <div className="useroffer-grid">
          {filteredOffers.map(renderCard)}
        </div>
      ) : (
        /* Default: grouped sections by level */
        LEVEL_SECTIONS.map(({ level, heading }) => {
          const group = filteredOffers.filter(o => o.level === level);
          if (group.length === 0) return null;
          return (
            <div key={level} className="offer-level-section">
              <h3 className="offer-section-heading">{heading}</h3>
              <div className="useroffer-grid">
                {group.map(renderCard)}
              </div>
            </div>
          );
        })
      )}

      <PreOrderModal
        open={preOrderOpen}
        onClose={() => { setPreOrderOpen(false); setSelectedOffer(null); }}
        offer={selectedOffer}
      />

      <ClaimOfferModal
        open={claimOpen}
        onClose={() => { setClaimOpen(false); setClaimOffer(null); }}
        offer={claimOffer}
      />
    </div>
  );
};

export default UserOffer;
